import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import Environment from '@config/environment';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@context/analytics/analytics.context';
import { CF_RESIZING_PREFIX } from '@services/image-service';
import styles from './landing.module.scss';

const benefits = [
  {
    icon: 'payment',
    title: 'PRO_SELLERS.LANDING.BENEFITS.PAYMENT.TITLE',
    text: 'PRO_SELLERS.LANDING.BENEFITS.PAYMENT.TEXT',
  },
  {
    icon: 'thumb-up',
    title: 'PRO_SELLERS.LANDING.BENEFITS.SHIPPING.TITLE',
    text: 'PRO_SELLERS.LANDING.BENEFITS.SHIPPING.TEXT',
  },
  {
    icon: 'chrono',
    title: 'PRO_SELLERS.LANDING.BENEFITS.VISIBILITY.TITLE',
    text: 'PRO_SELLERS.LANDING.BENEFITS.VISIBILITY.TEXT',
  },
  {
    icon: 'account-manager',
    title: 'PRO_SELLERS.LANDING.BENEFITS.TAILORED.TITLE',
    text: 'PRO_SELLERS.LANDING.BENEFITS.TAILORED.TEXT',
  },
];

const sellerStories = [
  {
    image: `${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=96,h=31,dpr=2/images/pages/landings/professional-sellers/suigeneris-logo.png`,
    title: 'Sui Generis',
    country: 'PRO_SELLERS.LANDING.STORIES.SUI_GENERIS.COUNTRY',
    text: 'PRO_SELLERS.LANDING.STORIES.SUI_GENERIS.TEXT',
    imageWidth: 96,
    imageHeight: 31,
  },
  {
    image: `${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=96,h=36,dpr=2/images/pages/landings/professional-sellers/brandear-logo.png`,
    title: 'Brandear',
    country: 'PRO_SELLERS.LANDING.STORIES.BRANDEAR.COUNTRY',
    text: 'PRO_SELLERS.LANDING.STORIES.BRANDEAR.TEXT',
    imageWidth: 96,
    imageHeight: 36,
  },
  {
    image: `${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=94,h=42,dpr=2/images/pages/landings/professional-sellers/trhe-brand-collector-logo.png`,
    title: 'The Brand Collector',
    country: 'PRO_SELLERS.LANDING.STORIES.THE_BRAND_COLLECTOR.COUNTRY',
    text: 'PRO_SELLERS.LANDING.STORIES.THE_BRAND_COLLECTOR.TEXT',
    imageWidth: 94,
    imageHeight: 42,
  },
];

type ProfessionalSellersLandingProps = {
  onCTAClick: (clickLocation: 'header' | 'footer') => void;
};

const ProfessionalSellersLanding: React.FC<ProfessionalSellersLandingProps> = ({ onCTAClick }) => {
  const { t } = useTranslation('professional-sellers');
  const { sendEvent } = useAnalytics();

  // title and image refs are used to manipulate the title position for longer titles in other languages
  // TODO: find out if this is still needed
  const titleRef = useRef<HTMLHeadingElement>(null);
  const imageTopRef = useRef<HTMLImageElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);

  const [activeBullet, setActiveBullet] = useState<number>(1);

  const handleScroll = () => {
    const srollElementWidth = slideRef?.current?.clientWidth || 327;
    setTimeout(() => {
      const scrollLeftOffest = slideRef?.current?.scrollLeft;
      const itemIndex = Math.round(scrollLeftOffest / srollElementWidth) + 1;
      setActiveBullet(itemIndex);
    }, 500);
  };

  const handleScrollTo = (itemIndex: number) => {
    const srollElementWidth = slideRef?.current?.clientWidth || 327;
    const xCoordinate = (itemIndex - 1) * srollElementWidth;
    slideRef.current.scrollTo(xCoordinate, 0);
    setActiveBullet(itemIndex);
  };

  const handleSendClickEvent = (buttonLocation: 'header' | 'footer') => {
    sendEvent({
      type: 'event',
      payload: {
        event: 'professional_seller_landing',
        button_label: 'send',
        button_location: buttonLocation,
      },
    });
  };

  return (
    <>
      <section className={clsx(styles.proSellers__intro)}>
        <div className={clsx('innerContainer', styles.proSellers__intro__container)}>
          <h1
            ref={titleRef}
            className={clsx(styles.proSellers__intro__title)}
          >
            {t('PRO_SELLERS.LANDING.INTRO.TITLE_PART_1')}{' '}
            <span className={clsx(styles.proSellers__intro__title__border)}>
              {t('PRO_SELLERS.LANDING.INTRO.TITLE_PART_2')}
            </span>
          </h1>
          <img
            ref={imageTopRef}
            className={clsx(styles.proSellers__intro__image)}
            srcSet={`
              ${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=304,h=170,dpr=2/images/pages/landings/professional-sellers/professional-sellers-1.jpg 304w,
              ${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=464,h=259,dpr=2/images/pages/landings/professional-sellers/professional-sellers-1.jpg 464w,
              ${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=554,h=308,dpr=2/images/pages/landings/professional-sellers/professional-sellers-1.jpg 554w
            `}
            sizes="(min-width: 1024px) 554px,(min-width: 768px) 464px, 304px"
            src={`
              ${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=554,h=308,dpr=2/images/pages/landings/professional-sellers/professional-sellers-1.jpg
            `}
            width="554"
            height="308"
            alt=" "
          />
          <div className={clsx(styles.proSellers__intro__text)}>
            <p>{t('PRO_SELLERS.LANDING.INTRO.TEXT')}</p>
            <button
              type="button"
              className={clsx(styles.proSellers__intro__text__btn, 'vc-btn', 'vc-btn--primary', 'vc-btn--large')}
              onClick={() => onCTAClick('header')}
            >
              {t('PRO_SELLERS.LANDING.INTRO.CTA')}
            </button>
          </div>
        </div>
      </section>
      <section className={clsx(styles.proSellers__benefits, 'innerContainer')}>
        <h2 className={clsx(styles.proSellers__benefits__title, 'vc-title-s')}>
          {t('PRO_SELLERS.LANDING.BENEFITS.TITLE')}
        </h2>
        {benefits.map((benefit) => (
          <article
            key={t(benefit.title)}
            className={clsx(styles.proSellers__benefits__article)}
          >
            <Icon
              className={clsx(styles.proSellers__benefits__article__icon)}
              name={benefit.icon}
              ariaHidden
            />
            <h3 className={clsx(styles.proSellers__benefits__article__title, 'vc-text-l')}>{t(benefit.title)}</h3>
            <p className={clsx(styles.proSellers__benefits__article__text, 'vc-text-m')}>{t(benefit.text)}</p>
          </article>
        ))}
      </section>
      <section className={clsx(styles.proSellers__qualify)}>
        <div className={clsx(styles.proSellers__qualify__container, 'innerContainer')}>
          <figure className={clsx(styles.proSellers__qualify__figure)}>
            <img
              className={clsx(styles.proSellers__qualify__image)}
              srcSet={`
              ${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=327,h=216,dpr=2/images/pages/landings/professional-sellers/professional-sellers-2.jpg 327w,
              ${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=403,h=270,dpr=2/images/pages/landings/professional-sellers/professional-sellers-2.jpg 403w
              `}
              sizes="(min-width: 768px) 403px, 327px"
              src={`${Environment.assetsPath}${CF_RESIZING_PREFIX}/f=auto,w=403,h=270,dpr=2/images/pages/landings/professional-sellers/professional-sellers-2.jpg`}
              alt=" "
              width="403"
              height="270"
            />
          </figure>
          <div>
            <h2 className={clsx(styles.proSellers__qualify__title, 'vc-title-s')}>
              {t('PRO_SELLERS.LANDING.QUALIFY.TITLE')}
            </h2>
            <p className={clsx(styles.proSellers__qualify__text)}>{t('PRO_SELLERS.LANDING.QUALIFY.TEXT')}</p>
            <button
              type="button"
              className={clsx(styles.proSellers__qualify__cta, 'vc-btn', 'vc-btn--primary', 'vc-btn--large')}
              onClick={() => onCTAClick('footer')}
            >
              {t('PRO_SELLERS.LANDING.QUALIFY.CTA')}
            </button>
          </div>
        </div>
      </section>
      <section className={clsx(styles.proSellers__stories, 'innerContainer')}>
        <h2 className={clsx(styles.proSellers__stories__title, 'vc-title-s')}>
          {t('PRO_SELLERS.LANDING.STORIES.TITLE')}
        </h2>
        <div
          className={clsx(styles.proSellers__slider)}
          ref={slideRef}
          onScroll={handleScroll}
        >
          <div className={clsx(styles.proSellers__slider__container)}>
            {sellerStories.map((story, index) => (
              <article
                key={t(story.country)}
                className={clsx(styles.proSellers__slider__slide)}
                ref={index === 0 ? slideRef : null}
              >
                <h3
                  className={clsx(styles.proSellers__slider__slide__logo)}
                  aria-label={t(story.title)}
                >
                  <img
                    src={story.image}
                    alt={t(story.title)}
                    width={story.imageWidth}
                    height={story.imageHeight}
                  />
                </h3>
                <p className={clsx(styles.proSellers__slider__slide__country, 'vc-text-l')}>{t(story.country)}</p>
                <p className={clsx(styles.proSellers__slider__slide__text, 'vc-text-m')}>{t(story.text)}</p>
              </article>
            ))}
          </div>
        </div>
        <ul
          className={clsx(styles.proSellers__slider__bullets)}
          aria-hidden="true"
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              role="button"
              tabIndex={index + 1}
              className={clsx(styles.proSellers__slider__bullets__item, activeBullet === index + 1 && styles.active)}
              onClick={() => handleScrollTo(index + 1)}
              onKeyDown={() => handleScrollTo(index + 1)}
            ></div>
          ))}
        </ul>
      </section>
      <section className={clsx(styles.proSellers__lastInfos, 'innerContainer')}>
        <h2 className={clsx(styles.proSellers__lastInfos__title, 'vc-title-s')}>
          {t('PRO_SELLERS.LANDING.LAST_INFOS.TITLE')}
        </h2>
        <p className={clsx(styles.proSellers__lastInfos__helpText, 'vc-text-m')}>
          {t('PRO_SELLERS.LANDING.LAST_INFOS.HELP_SECTION_START') + ' '}
          <a
            target="_blank"
            className={clsx('vc-link')}
            rel="noreferrer"
            onClick={() => handleSendClickEvent('footer')}
            href={t('PRO_SELLERS.LANDING.LAST_INFOS.HELP.LINK_URL')}
          >
            {t('PRO_SELLERS.LANDING.LAST_INFOS.HELP_SECTION_LINK_TEXT') + ' '}
          </a>
          {t('PRO_SELLERS.LANDING.LAST_INFOS.HELP_SECTION_END')}
        </p>
        <p className={clsx(styles.proSellers__lastInfos__legal, 'vc-text-s')}>
          {t('PRO_SELLERS.LANDING.LAST_INFOS.LEGAL_SECTION_START') + ' '}
          <a
            target="_blank"
            className={clsx('vc-link')}
            rel="noreferrer"
            onClick={() => handleSendClickEvent('footer')}
            href={t('PRO_SELLERS.LANDING.LAST_INFOS.LEGAL.LINK_URL')}
          >
            {t('PRO_SELLERS.LANDING.LAST_INFOS.HELP_SECTION_LINK_TEXT') + ' '}
          </a>
          {t('PRO_SELLERS.LANDING.LAST_INFOS.LEGAL_SECTION_END')}
        </p>
      </section>
    </>
  );
};

export default ProfessionalSellersLanding;
